import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, MaskedLine } from "../components/Reveal";
import PasswordField from "../components/PasswordField";

const EMPTY_REG = {
  nome: "", cognome: "", email: "", telefono: "", password: "",
  terms_accepted: false, captcha_answer: "",
};

export default function Accedi() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "registrati" ? "registrati" : "accedi");
  const refCode = params.get("ref") || "";

  const { user, setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [login, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState(EMPTY_REG);
  const [captcha, setCaptcha] = useState(null);
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : "/area", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (mode === "registrati") {
      api.get("/captcha").then((res) => setCaptcha(res.data)).catch(() => setCaptcha(null));
    }
  }, [mode]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", login);
      setUser(data);
      navigate(data.role === "admin" ? "/admin" : "/area");
    } catch (err) {
      setError(formatApiError(err, "Accesso non riuscito. Verifica le credenziali."));
    } finally {
      setBusy(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/register", {
        ...reg,
        captcha_token: captcha?.token || "",
        referral_code: refCode || undefined,
      });
      setRegistered(true);
    } catch (err) {
      setError(formatApiError(err, "Registrazione non riuscita. Riprova."));
      api.get("/captcha").then((res) => setCaptcha(res.data)).catch(() => {});
      setReg((r) => ({ ...r, captcha_answer: "" }));
    } finally {
      setBusy(false);
    }
  };

  const setRegField = (k) => (e) =>
    setReg((r) => ({ ...r, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const tab = (value, label, testid) => (
    <button
      key={value}
      onClick={() => { setMode(value); setError(""); }}
      className={`flex-1 py-3.5 text-sm font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
        mode === value ? "bg-pine text-cream" : "text-ink-muted hover:text-pine"
      }`}
      data-testid={testid}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-28" data-testid="accedi-page">
      <div className="max-w-xl mx-auto">
        <MaskedLine delay={0.1}><span className="eyebrow">{t("ac_eyebrow")}</span></MaskedLine>
        <h1 className="mt-6 text-4xl sm:text-5xl leading-tight" data-testid="accedi-title">
          <MaskedLine delay={0.25}>
            {mode === "accedi"
              ? <>{t("ac_login_pre")} <em className="italic text-gold">{t("ac_login_em")}</em>.</>
              : <>{t("ac_reg_pre")} <em className="italic text-gold">{t("ac_reg_em")}</em>.</>}
          </MaskedLine>
        </h1>

        <Reveal delay={0.35}>
          <div className="mt-10 card-surface" data-testid="auth-card">
            <div className="flex border-b border-pine/10">
              {tab("accedi", t("ac_tab1"), "tab-login")}
              {tab("registrati", t("ac_tab2"), "tab-register")}
            </div>

            {mode === "accedi" ? (
              <form onSubmit={submitLogin} className="p-8 sm:p-10 space-y-6" data-testid="login-form">
                <div>
                  <label htmlFor="l-email" className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-muted mb-2">{t("ac_email")}</label>
                  <input id="l-email" type="email" className="input-field" required value={login.email}
                    onChange={(e) => setLogin((l) => ({ ...l, email: e.target.value }))}
                    data-testid="login-email" autoComplete="email" />
                </div>
                <PasswordField id="l-password" label={t("ac_pwd")} value={login.password}
                  onChange={(e) => setLogin((l) => ({ ...l, password: e.target.value }))}
                  testid="login-password" autoComplete="current-password" />
                {error && <p className="text-sm text-bordeaux" role="alert" data-testid="login-error">{error}</p>}
                <button type="submit" className="btn-primary w-full disabled:opacity-60" disabled={busy} data-testid="login-submit">
                  {busy ? t("ac_busy") : t("ac_btn")}
                </button>
                <Link to="/recupera-password" className="block text-center text-sm text-pine underline underline-offset-4 hover:text-gold transition-colors" data-testid="login-forgot-link">
                  {t("ac_forgot")}
                </Link>
              </form>
            ) : registered ? (
              <div className="p-8 sm:p-10 border-s-2 !border-s-gold" data-testid="register-success">
                <p className="font-serif text-3xl text-ink">{t("ac_ok_t")}</p>
                <p className="mt-4 text-lg text-ink-muted leading-relaxed">{t("ac_ok_x")}</p>
              </div>
            ) : (
              <form onSubmit={submitRegister} className="p-8 sm:p-10 space-y-6" data-testid="register-form">
                {refCode && (
                  <p className="text-sm text-pine bg-pine-soft px-4 py-3" data-testid="register-ref-notice">
                    {t("ac_ref")} {refCode}).
                  </p>
                )}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="r-nome" className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-muted mb-2">{t("ac_nome")}</label>
                    <input id="r-nome" className="input-field" required value={reg.nome} onChange={setRegField("nome")} data-testid="register-nome" autoComplete="given-name" />
                  </div>
                  <div>
                    <label htmlFor="r-cognome" className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-muted mb-2">{t("ac_cognome")}</label>
                    <input id="r-cognome" className="input-field" required value={reg.cognome} onChange={setRegField("cognome")} data-testid="register-cognome" autoComplete="family-name" />
                  </div>
                </div>
                <div>
                  <label htmlFor="r-email" className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-muted mb-2">{t("ac_email")}</label>
                  <input id="r-email" type="email" className="input-field" required value={reg.email} onChange={setRegField("email")} data-testid="register-email" autoComplete="email" />
                </div>
                <div>
                  <label htmlFor="r-telefono" className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-muted mb-2">{t("ac_tel")}</label>
                  <input id="r-telefono" type="tel" className="input-field" required value={reg.telefono} onChange={setRegField("telefono")} data-testid="register-telefono" autoComplete="tel" />
                </div>
                <PasswordField id="r-password" label={t("ac_pwd")} value={reg.password}
                  onChange={setRegField("password")} testid="register-password" autoComplete="new-password"
                  hint={t("ac_hint")} />
                <div className="border border-pine/15 bg-cream px-5 py-4">
                  <label htmlFor="r-captcha" className="block text-xs font-semibold uppercase tracking-[0.2em] text-ink-muted mb-2">
                    {t("ac_captcha")}{captcha ? `: ${captcha.question}` : ""}
                  </label>
                  <input id="r-captcha" inputMode="numeric" className="input-field !bg-surface" required
                    value={reg.captcha_answer} onChange={setRegField("captcha_answer")} data-testid="register-captcha" placeholder="—" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="register-terms-label">
                  <input type="checkbox" className="mt-1.5 h-4 w-4 accent-[#0E5C59]" checked={reg.terms_accepted}
                    onChange={setRegField("terms_accepted")} data-testid="register-terms" required />
                  <span className="text-sm text-ink-muted leading-relaxed">
                    {t("ac_terms_a")} <Link to="/legali/termini" className="text-pine underline underline-offset-4 hover:text-gold">{t("ac_terms_b")}</Link> {t("ac_terms_c")}.
                  </span>
                </label>
                {error && <p className="text-sm text-bordeaux" role="alert" data-testid="register-error">{error}</p>}
                <button type="submit" className="btn-primary w-full disabled:opacity-60" disabled={busy || !captcha} data-testid="register-submit">
                  {busy ? t("ac_regbusy") : t("ac_regbtn")}
                </button>
                <p className="text-xs text-ink-muted leading-relaxed">{t("ac_note")}</p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
